'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ISearchParams } from '@/ts/interfaces';
import { cn } from '@/helpers/common';
import { MAX_PAGINATION_PAGE, normalizePaginationPage } from '@/constants/pagination';
import IconArrowThin from '@/components/svgs/icons/arrow-icon-thin';

interface PaginationJCProps {
  currentPage: number;
  searchParams: ISearchParams;
  disableNext?: boolean;
  totalPages?: number;
}

const PaginationJC: React.FC<PaginationJCProps> = ({
  currentPage: requestedPage,
  searchParams,
  disableNext = false,
  totalPages = MAX_PAGINATION_PAGE,
}) => {
  const [pages, setPages] = useState<number[]>([]);
  const safeTotalPages = Math.min(MAX_PAGINATION_PAGE, Math.max(1, totalPages));
  const currentPage = normalizePaginationPage(requestedPage, safeTotalPages);
  const pathname = usePathname();

  const buildPaginationLink = (page: number) => {
    const queryParams = new URLSearchParams(searchParams);
    const safePage = normalizePaginationPage(page, safeTotalPages);

    if (safePage > 1) queryParams.set('page', safePage.toString());
    else queryParams.delete('page');

    const query = queryParams.toString();
    return query ? `${pathname}?${query}` : pathname;
  };

  useEffect(() => {
    const pagesArr = [];
    const maxPagesToShow = 5;

    pagesArr.push(1);

    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(safeTotalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(safeTotalPages - 1, maxPagesToShow);
    }
    if (currentPage + 2 >= safeTotalPages) {
      startPage = Math.max(2, safeTotalPages - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      if (currentPage - startPage >= 2 && safeTotalPages > 5 && i === startPage) {
        // pagesArr.push({ page: i, text: "...", link: `?page=${i}` });
      } else if (endPage - currentPage >= 2 && safeTotalPages > 5 && i === endPage) {
        // pagesArr.push({ page: i, text: "...", link: `?page=${i}` });
      } else {
        pagesArr.push(i);
      }
    }
    if (safeTotalPages > 1 && !pagesArr.find((p) => p === safeTotalPages)) {
      pagesArr.push(safeTotalPages);
    }

    setPages(pagesArr);
  }, [currentPage, safeTotalPages]);

  return (
    <nav aria-label="Pagination" className={'flex justify-center gap-2 mt-4 max-md:px-0 md:px-4'}>
      <Link
        aria-label="Previous page"
        aria-disabled={currentPage <= 1}
        tabIndex={currentPage <= 1 ? -1 : undefined}
        href={buildPaginationLink(currentPage - 1)}
        className={cn(
          'inline-flex h-9 w-9 justify-center items-center no-underline text-jc-firefly text-sm leading-none default-transition',
          'rounded-md border border-jc-botticelli bg-white hover:border-jc-firefly!',
          {
            'pointer-events-none cursor-default opacity-50': currentPage <= 1,
          },
        )}
      >
        <IconArrowThin
          aria-hidden="true"
          className="w-1.75 h-3.25 [&_path]:stroke-jc-firefly rotate-180"
        />
      </Link>
      {pages.map((p) => (
        <Link
          key={p}
          href={buildPaginationLink(p)}
          aria-label={`Page ${p}`}
          aria-current={currentPage === p ? 'page' : undefined}
          className={cn(
            'inline-flex h-9 w-9 justify-center items-center no-underline text-jc-firefly text-sm leading-none default-transition',
            'rounded-md border border-jc-botticelli bg-white hover:border-jc-firefly!',
            {
              'bg-jc-firefly! border-jc-firefly! text-white! font-bold! pointer-events-none cursor-default':
                currentPage === p,
            },
          )}
        >
          {p}
        </Link>
      ))}

      <Link
        href={buildPaginationLink(currentPage + 1)}
        aria-label="Next page"
        aria-disabled={currentPage >= safeTotalPages || disableNext}
        tabIndex={currentPage >= safeTotalPages || disableNext ? -1 : undefined}
        className={cn(
          'inline-flex h-9 w-9 justify-center items-center no-underline text-jc-firefly text-sm leading-none default-transition',
          'rounded-md border border-jc-botticelli bg-white hover:border-jc-firefly!',
          {
            'pointer-events-none cursor-default opacity-50':
              currentPage >= safeTotalPages || disableNext,
          },
        )}
      >
        <IconArrowThin aria-hidden="true" className="w-1.75 h-3.25 [&_path]:stroke-jc-firefly" />
      </Link>
    </nav>
  );
};

export default PaginationJC;
